import { Injectable } from '@angular/core';
import {
  ChatMessage,
  ConversationItem,
  RateLimit,
  ViaQuestion
} from '@cpd-app/core-module/models';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import * as AiChatActions from './ai-chat.actions';
import * as AiChatSelectors from './ai-chat.selectors';
import { AiChatState } from './ai-chat.state';

@Injectable({
  providedIn: 'root'
})
export class AiChatFacade {
  private triggerTooltipSubject$ = new Subject<void>();

  constructor(
    private store: Store<AiChatState>,
    private actions$: Actions
  ) {}

  public triggerTooltip$: Observable<void> =
    this.triggerTooltipSubject$.asObservable();

  public currentConversationId$: Observable<number | null> = this.store.select(
    AiChatSelectors.selectCurrentConversationId
  );

  public currentConversation$: Observable<ConversationItem | null> =
    this.store.select(AiChatSelectors.selectCurrentConversation);

  public messages$: Observable<ChatMessage[]> = this.store.select(
    AiChatSelectors.selectMessages
  );

  public conversations$: Observable<ConversationItem[]> = this.store.select(
    AiChatSelectors.selectConversations
  );

  public isLoading$: Observable<boolean> = this.store.select(
    AiChatSelectors.selectIsLoading
  );

  public error$: Observable<string | null> = this.store.select(
    AiChatSelectors.selectError
  );

  public isStreaming$: Observable<boolean> = this.store.select(
    AiChatSelectors.selectIsStreaming
  );

  public streamingResponse$: Observable<string> = this.store.select(
    AiChatSelectors.selectStreamingResponse
  );

  public conversationsLoading$: Observable<boolean> = this.store.select(
    AiChatSelectors.selectConversationsLoading
  );

  public conversationsCount$: Observable<number> = this.store.select(
    AiChatSelectors.selectConversationsCount
  );

  public conversationsLimit$: Observable<number> = this.store.select(
    AiChatSelectors.selectConversationsLimit
  );

  public conversationsOffset$: Observable<number> = this.store.select(
    AiChatSelectors.selectConversationsOffset
  );

  public rateLimit$: Observable<RateLimit | null> = this.store.select(
    AiChatSelectors.selectRateLimit
  );

  public isOpenChatVia$: Observable<boolean> = this.store.select(
    AiChatSelectors.selectIsOpenChatVia
  );

  public streamConnected$ = this.actions$.pipe(
    ofType(AiChatActions.streamConnected)
  );

  public streamChunk$ = this.actions$.pipe(ofType(AiChatActions.streamChunk));

  public streamStart$ = this.actions$.pipe(ofType(AiChatActions.streamStart));

  public streamComplete$ = this.actions$.pipe(
    ofType(AiChatActions.streamComplete)
  );

  public streamFailure$ = this.actions$.pipe(
    ofType(AiChatActions.streamFailure)
  );

  public getConversationHistorySuccess$ = this.actions$.pipe(
    ofType(AiChatActions.getConversationHistorySuccess)
  );

  public loadConversationsSuccess$ = this.actions$.pipe(
    ofType(AiChatActions.loadConversationsSuccess)
  );

  public askQuestion$ = this.actions$.pipe(ofType(AiChatActions.askQuestion));
  public openChatVia$ = this.actions$.pipe(ofType(AiChatActions.openChatVia));
  public closeChatVia$ = this.actions$.pipe(ofType(AiChatActions.closeChatVia));

  public getConversationHistory(
    conversationId: number,
    limit = 1000,
    offset = 0
  ): void {
    this.store.dispatch(
      AiChatActions.getConversationHistory({ conversationId, limit, offset })
    );
  }

  public loadConversations(offset: number, limit: number): void {
    this.store.dispatch(AiChatActions.loadConversations({ offset, limit }));
  }

  public setCurrentConversation(conversationId: number | null): void {
    this.store.dispatch(
      AiChatActions.setCurrentConversation({ conversationId })
    );
  }

  public newChat(): void {
    this.store.dispatch(AiChatActions.newChat());
  }

  public deleteConversation(conversationId: number): void {
    this.store.dispatch(AiChatActions.deleteConversation({ conversationId }));
  }

  public resetAiChatState(): void {
    this.store.dispatch(AiChatActions.resetAiChatState());
  }

  public streamStart(query: string, question?: ViaQuestion): void {
    this.store.dispatch(AiChatActions.streamStart({ query, question }));
  }

  public getRateLimit(): void {
    this.store.dispatch(AiChatActions.getRateLimit());
  }

  public askQuestion(question: ViaQuestion): void {
    this.store.dispatch(AiChatActions.askQuestion({ question }));
  }

  /** `auto: true` only for an open the user did not ask for; see the action. */
  public openChatVia(options?: { auto?: boolean }): void {
    this.store.dispatch(AiChatActions.openChatVia(options));
  }

  /** T.8. Shows a message from Via, with no model call; see `viaSays`. */
  public viaSays(content: string): void {
    this.store.dispatch(AiChatActions.viaSays({ content }));
  }

  public openChatViaToolTip(): void {
    this.triggerTooltipSubject$.next();
  }

  public closeChatVia(): void {
    this.store.dispatch(AiChatActions.closeChatVia());
  }

  public likeMessage(messageId: number, liked: boolean): void {
    this.store.dispatch(AiChatActions.likeMessage({ messageId, liked }));
  }

  public dislikeMessage(messageId: number, disliked: boolean): void {
    this.store.dispatch(AiChatActions.dislikeMessage({ messageId, disliked }));
  }
}
